import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaMapMarkerAlt, FaCalendarAlt, FaExternalLinkAlt } from 'react-icons/fa';
import { useLanguage } from '../utils/LanguageContext';
import './AllComponents.css';

const Experience = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const { t } = useLanguage();

  const experiences = [
    {
      company: "mVISE AG",
      position: t('experience.experiences.mvise.position'),
      location: t('experience.experiences.mvise.location'),
      period: "Oct 2022 - Present",
      description: t('experience.experiences.mvise.description'),
      technologies: ["JavaScript", "Node.js", "Apigee", "AWS Lambda", "Amazon S3", "GCP", "Go", "JIRA"],
      logo: "/api/profile/mvise.png",
      website: "https://mvise.de"
    },
    {
      company: "TU Dortmund University",
      position: t('experience.experiences.tudortmund1.position'),
      location: t('experience.experiences.tudortmund1.location'),
      period: "Oct 2023 - Oct 2024",
      description: t('experience.experiences.tudortmund1.description'),
      technologies: ["Python", "LLM", "Information Retrieval", "Embeddings", "AI/ML", "Version Control"],
      logo: "/api/profile/tudortmund.png",
      website: "https://tu-dortmund.de"
    },
    {
      company: "TU Dortmund University",
      position: t('experience.experiences.tudortmund2.position'),
      location: t('experience.experiences.tudortmund2.location'),
      period: "Dec 2021 - Sep 2022",
      description: t('experience.experiences.tudortmund2.description'),
      technologies: ["Bash", "LDAP", "Docker", "OpenNebula", "MySQL", "phpMyAdmin", "Linux"],
      logo: "/api/profile/tudortmund.png",
      website: "https://tu-dortmund.de"
    },
    {
      company: "Kultur Ruhr GmbH",
      position: t('experience.experiences.kultur.position'),
      location: t('experience.experiences.kultur.location'),
      period: "Jul 2021 - Feb 2022",
      description: t('experience.experiences.kultur.description'),
      technologies: ["Active Directory", "Windows Server", "Network Management", "IT Security", "Backup Solutions"],
      logo: "/api/profile/kultur.png",
      website: "https://www.rvr.ruhr/"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="experience" className="section experience-section">
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div variants={itemVariants} className="section-header text-center">
            <h2 className="gradient-text">{t('experience.workExperience')}</h2>
            <p className="section-subtitle">
              {t('experience.workSubtitle')}
            </p>
          </motion.div>

          <div className="timeline">
            {experiences.map((exp, index) => (
              <motion.div
                key={index}
                className="timeline-item"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <div className="timeline-marker"></div>
                <div className="experience-card card">
                  <div className="experience-header">
                    <div className="company-info">
                      <img 
                        src={exp.logo} 
                        alt={`${exp.company} logo`}
                        className="company-logo"
                      />
                      <div>
                        <h3>{exp.position}</h3>
                        <h4>
                          <a href={exp.website} target="_blank" rel="noopener noreferrer">
                            {exp.company}
                            <FaExternalLinkAlt />
                          </a>
                        </h4>
                      </div>
                    </div>
                    <div className="experience-meta">
                      <div className="meta-item">
                        <FaCalendarAlt />
                        <span>{exp.period}</span>
                      </div>
                      <div className="meta-item">
                        <FaMapMarkerAlt />
                        <span>{exp.location}</span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="experience-description">{exp.description}</p>
                  
                  <div className="technologies">
                    {exp.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience; 